/**
 * CaseCustomFieldsEditor - Refactored Version
 * 
 * Manages custom field values for a specific case.
 * Supports field dependencies, calculations, validation, and audit trails.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import { 
  Settings, Save, Loader2, AlertCircle, ChevronDown, ChevronRight,
  CheckCircle2, Eye, EyeOff, Info, GitBranch, Link2, History
} from 'lucide-react';

// Import extracted components and constants
import { groupColors, getGroupIcon } from './templates/templateConstants';
import CalculationAuditDialog from './fields/CalculationAuditDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Local group colors variant for case editor
const caseGroupColors = {
  blue: 'bg-blue-50 border-blue-200 text-blue-800',
  red: 'bg-red-50 border-red-200 text-red-800',
  green: 'bg-green-50 border-green-200 text-green-800',
  purple: 'bg-purple-50 border-purple-200 text-purple-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  slate: 'bg-slate-50 border-slate-200 text-slate-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  pink: 'bg-pink-50 border-pink-200 text-pink-800',
  cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800'
};

export default function CaseCustomFieldsEditor({ caseId, caseType = '', onUpdate }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [showOnlyRelevant, setShowOnlyRelevant] = useState(false);
  
  // Audit dialog state
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditDialogField, setAuditDialogField] = useState(null);
  const [currentCalcValue, setCurrentCalcValue] = useState(null);
  const [currentSourceFields, setCurrentSourceFields] = useState([]);

  const allFields = data?.all_fields || [];
  const groupedFields = data?.grouped_fields || [];

  // Compute field relevance and completeness
  const { requiredFields, completenessStats } = useMemo(() => {
    const required = [];
    
    allFields.forEach(field => {
      const validation = field.validation || {};
      const requiredForTypes = validation.required_for_case_types || [];
      const isRequiredForCaseType = requiredForTypes.length > 0 && caseType && requiredForTypes.includes(caseType);
      const isGloballyRequired = field.required;
      
      if (isRequiredForCaseType || isGloballyRequired) {
        required.push(field.tag);
      }
    });
    
    const totalRequired = required.length;
    const filledRequired = required.filter(tag => {
      const value = values[tag];
      return value !== undefined && value !== null && value !== '';
    }).length;
    
    const percentage = totalRequired > 0 ? Math.round((filledRequired / totalRequired) * 100) : 100;
    
    return {
      requiredFields: required,
      completenessStats: { total: totalRequired, filled: filledRequired, percentage }
    };
  }, [allFields, caseType, values]);

  // Check if a field is required
  const isFieldRequired = useCallback((field) => {
    const validation = field.validation || {};
    const requiredForTypes = validation.required_for_case_types || [];
    return field.required || (requiredForTypes.length > 0 && caseType && requiredForTypes.includes(caseType));
  }, [caseType]);

  // Check if a field is relevant to current case type
  const isFieldRelevant = useCallback((field) => {
    const validation = field.validation || {};
    const requiredForTypes = validation.required_for_case_types || [];
    if (requiredForTypes.length === 0) return true;
    return caseType && requiredForTypes.includes(caseType);
  }, [caseType]);

  // Check if dependency is met (supports chains)
  const checkDependencyMet = useCallback((field, visitedTags = new Set()) => {
    const dependency = field.dependency;
    if (!dependency || !dependency.depends_on) return true;
    
    if (visitedTags.has(field.tag)) return false;
    visitedTags.add(field.tag);
    
    const parentField = allFields.find(f => f.tag === dependency.depends_on);
    if (parentField && !checkDependencyMet(parentField, visitedTags)) {
      return false;
    }
    
    const dependsOnValue = values[dependency.depends_on];
    const actualStr = dependsOnValue ? String(dependsOnValue) : '';
    const expectedValue = dependency.value || '';
    const condition = dependency.condition || 'exists';
    
    switch (condition) {
      case 'equals': return actualStr.toLowerCase() === expectedValue.toLowerCase();
      case 'not_equals': return actualStr.toLowerCase() !== expectedValue.toLowerCase();
      case 'exists': return Boolean(actualStr);
      case 'not_exists': return !Boolean(actualStr);
      case 'contains': return actualStr.toLowerCase().includes(expectedValue.toLowerCase());
      case 'greater_than': return !isNaN(actualStr) && !isNaN(expectedValue) && parseFloat(actualStr) > parseFloat(expectedValue);
      case 'less_than': return !isNaN(actualStr) && !isNaN(expectedValue) && parseFloat(actualStr) < parseFloat(expectedValue);
      default: return true;
    }
  }, [allFields, values]);

  // Get dependency info for tooltip
  const getDependencyInfo = useCallback((field) => {
    const dependency = field.dependency;
    if (!dependency || !dependency.depends_on) return null;
    
    const dependsOnField = allFields.find(f => f.tag === dependency.depends_on);
    const dependsOnName = dependsOnField?.name || dependency.depends_on;
    
    return {
      dependsOnName,
      condition: dependency.condition,
      value: dependency.value,
      makeRequired: dependency.make_required
    };
  }, [allFields]);

  // Fetch custom fields
  const fetchCustomFields = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/custom-fields?grouped=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
        
        const initialValues = {};
        (result.all_fields || []).forEach(field => {
          initialValues[field.tag] = field.value ?? field.default_value ?? '';
        });
        setValues(initialValues);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId, token]);

  useEffect(() => {
    fetchCustomFields();
  }, [fetchCustomFields]);

  const handleValueChange = (tag, value) => {
    setValues(prev => ({ ...prev, [tag]: value }));
    setHasChanges(true);
    if (validationErrors[tag]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[tag];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setValidationErrors({});
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/custom-fields`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });

      const result = await res.json();
      
      if (res.ok && result.success !== false) {
        toast.success('Custom fields saved');
        setHasChanges(false);
        setValidationErrors({});
        if (onUpdate) onUpdate();
      } else if (result.validation_errors?.length > 0) {
        const errors = {};
        result.validation_errors.forEach(err => {
          errors[err.tag] = err.error;
        });
        setValidationErrors(errors);
        toast.error(`Validation failed: ${result.validation_errors.length} error(s)`);
      } else {
        toast.error('Failed to save custom fields');
      }
    } catch (error) {
      toast.error('Failed to save custom fields');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Open audit dialog for calculation field
  const openAuditDialog = (field, calculatedValue, sourceFields) => {
    setAuditDialogField(field);
    setCurrentCalcValue(calculatedValue);
    setCurrentSourceFields(sourceFields);
    setAuditDialogOpen(true);
  };

  // Calculate value for calculation fields
  const calculateFieldValue = useCallback((field) => {
    const calculation = field.calculation || {};
    const sourceFields = calculation.source_fields || [];
    
    const sourceValues = sourceFields.map(tag => {
      const val = values[tag];
      return parseFloat(val) || 0;
    });
    
    if (sourceValues.length === 0) return { value: 0, displayValue: '$0.00' };
    
    let calculatedValue = 0;
    const operator = calculation.operator || 'sum';
    
    switch (operator) {
      case 'sum': calculatedValue = sourceValues.reduce((a, b) => a + b, 0); break;
      case 'subtract': calculatedValue = sourceValues[0] - sourceValues.slice(1).reduce((a, b) => a + b, 0); break;
      case 'multiply': calculatedValue = sourceValues.reduce((a, b) => a * b, 1); break;
      case 'divide': calculatedValue = sourceValues.length >= 2 && sourceValues[1] !== 0 ? sourceValues[0] / sourceValues[1] : 0; break;
      case 'average': calculatedValue = sourceValues.reduce((a, b) => a + b, 0) / sourceValues.length; break;
      case 'min': calculatedValue = Math.min(...sourceValues); break;
      case 'max': calculatedValue = Math.max(...sourceValues); break;
      case 'percentage': calculatedValue = sourceValues.length >= 2 ? sourceValues[0] * (sourceValues[1] / 100) : 0; break;
      case 'custom':
        if (calculation.custom_formula) {
          try {
            let expr = calculation.custom_formula;
            sourceFields.forEach((tag, idx) => {
              expr = expr.replace(new RegExp(tag, 'g'), sourceValues[idx].toString());
            });
            calculatedValue = Function('"use strict"; return (' + expr + ')')();
          } catch { calculatedValue = 0; }
        }
        break;
      default: calculatedValue = sourceValues.reduce((a, b) => a + b, 0);
    }
    
    const roundTo = calculation.round_to ?? 2;
    calculatedValue = parseFloat(calculatedValue.toFixed(roundTo));
    
    const displayValue = calculation.format_as_currency
      ? `$${calculatedValue.toLocaleString('en-US', { minimumFractionDigits: roundTo, maximumFractionDigits: roundTo })}`
      : calculatedValue.toLocaleString('en-US', { minimumFractionDigits: roundTo, maximumFractionDigits: roundTo });
    
    return { value: calculatedValue, displayValue, sourceFields };
  }, [values]);

  // Render field input based on type
  const renderFieldInput = (field) => {
    const value = values[field.tag] ?? '';

    switch (field.field_type) {
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleValueChange(field.tag, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.name}...`}
            rows={3}
            data-testid={`custom-field-input-${field.tag}`}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(field.tag, e.target.value)}
            placeholder={field.placeholder || '0'}
            data-testid={`custom-field-input-${field.tag}`}
          />
        );

      case 'currency':
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => handleValueChange(field.tag, e.target.value)}
              placeholder={field.placeholder || '0.00'}
              className="pl-7"
              data-testid={`custom-field-input-${field.tag}`}
            />
          </div>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleValueChange(field.tag, e.target.value)}
            data-testid={`custom-field-input-${field.tag}`}
          />
        );

      case 'email':
        return (
          <Input
            type="email"
            value={value}
            onChange={(e) => handleValueChange(field.tag, e.target.value)}
            placeholder={field.placeholder || 'email@example.com'}
            data-testid={`custom-field-input-${field.tag}`}
          />
        );

      case 'phone':
        return (
          <Input
            type="tel"
            value={value}
            onChange={(e) => handleValueChange(field.tag, e.target.value)}
            placeholder={field.placeholder || '(555) 123-4567'}
            data-testid={`custom-field-input-${field.tag}`}
          />
        );

      case 'select':
        return (
          <Select value={value} onValueChange={(v) => handleValueChange(field.tag, v)}>
            <SelectTrigger data-testid={`custom-field-input-${field.tag}`}>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`checkbox-${field.tag}`}
              checked={value === true || value === 'true'}
              onCheckedChange={(checked) => handleValueChange(field.tag, checked)}
              data-testid={`custom-field-input-${field.tag}`}
            />
            <label htmlFor={`checkbox-${field.tag}`} className="text-sm text-muted-foreground cursor-pointer">
              {field.placeholder || field.description || 'Yes'}
            </label>
          </div>
        );

      case 'calculation':
        const { displayValue, sourceFields } = calculateFieldValue(field);
        const calculation = field.calculation || {};
        
        return (
          <div className="space-y-2">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid={`custom-field-input-${field.tag}`}>
              <div className="flex items-center justify-between">
                <div className="text-xl font-bold text-purple-800">{displayValue}</div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAuditDialog(field, displayValue, sourceFields)}
                        className="text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                        data-testid={`audit-btn-${field.tag}`}
                      >
                        <History className="w-4 h-4 mr-1" />
                        Audit
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View audit trail & record for court</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-xs text-purple-600 mt-1">
                Auto-calculated from: {sourceFields.join(' + ')}
              </div>
            </div>
            {calculation.description && (
              <p className="text-xs text-muted-foreground">{calculation.description}</p>
            )}
          </div>
        );

      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleValueChange(field.tag, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.name}...`}
            data-testid={`custom-field-input-${field.tag}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-muted-foreground">Loading custom fields...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (allFields.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <Settings className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No custom fields configured</p>
          <p className="text-xs text-muted-foreground mt-1">Create custom fields in Documents → Custom Fields</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with completeness and save */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600" />
            <span className="font-medium">Custom Fields</span>
          </div>
          {completenessStats.total > 0 && (
            <div className="flex items-center gap-2">
              <Progress value={completenessStats.percentage} className="w-24 h-2" />
              <span className="text-xs text-muted-foreground">
                {completenessStats.filled}/{completenessStats.total} required
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-relevant"
              checked={showOnlyRelevant}
              onCheckedChange={setShowOnlyRelevant}
            />
            <label htmlFor="show-relevant" className="text-xs text-muted-foreground cursor-pointer">
              Show only relevant fields
            </label>
          </div>
          <Button onClick={handleSave} disabled={saving || !hasChanges} size="sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Field Groups */}
      {groupedFields.map((group) => {
        const fields = group.fields || [];
        const visibleFields = fields.filter(field => {
          if (!checkDependencyMet(field)) return false;
          if (showOnlyRelevant && !isFieldRelevant(field)) return false;
          return true;
        });

        if (visibleFields.length === 0) return null;

        const IconComponent = getGroupIcon(group.icon);
        const isCollapsed = collapsedGroups[group.id];
        const colorClass = caseGroupColors[group.color] || caseGroupColors.slate;

        return (
          <Card key={group.id} className={`border ${colorClass.split(' ').find(c => c.startsWith('border-'))}`}>
            <CardHeader
              className={`cursor-pointer transition-colors ${colorClass} rounded-t-lg`}
              onClick={() => toggleGroup(group.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <IconComponent className="w-4 h-4" />
                  <CardTitle className="text-sm font-medium">{group.name}</CardTitle>
                  <Badge variant="secondary" className="text-xs">{visibleFields.length} fields</Badge>
                </div>
              </div>
            </CardHeader>
            {!isCollapsed && (
              <CardContent className="pt-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {visibleFields.map(field => {
                    const validation = field.validation || {};
                    const dependencyInfo = getDependencyInfo(field);
                    const fieldRequired = isFieldRequired(field);
                    const isFilled = values[field.tag] !== undefined && values[field.tag] !== '' && values[field.tag] !== null;
                    const isTypeSpecificRequired = validation.required_for_case_types?.includes(caseType) && !field.required;

                    return (
                      <div key={field.id} className="space-y-2">
                        <Label className="flex items-center gap-2 flex-wrap">
                          {field.name}
                          {fieldRequired && <span className="text-red-500">*</span>}
                          {dependencyInfo && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300 cursor-help">
                                    {dependencyInfo.chainDepth > 0 ? (
                                      <><GitBranch className="w-3 h-3 mr-1" />Chain</>
                                    ) : (
                                      <><Link2 className="w-3 h-3 mr-1" />Conditional</>
                                    )}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Shows when "{dependencyInfo.dependsOnName}" {
                                    dependencyInfo.condition === 'exists' ? 'has a value' :
                                    dependencyInfo.condition === 'not_exists' ? 'is empty' :
                                    dependencyInfo.condition === 'equals' ? `equals "${dependencyInfo.value}"` :
                                    dependencyInfo.condition === 'not_equals' ? `does not equal "${dependencyInfo.value}"` :
                                    dependencyInfo.condition === 'contains' ? `contains "${dependencyInfo.value}"` :
                                    dependencyInfo.condition === 'greater_than' ? `> ${dependencyInfo.value}` :
                                    `< ${dependencyInfo.value}`
                                  }</p>
                                  {dependencyInfo.makeRequired && <p className="text-amber-500">Required when shown</p>}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {isTypeSpecificRequired && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-300">
                              Required for {caseType.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {fieldRequired && isFilled && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          )}
                        </Label>
                        {field.description && (
                          <p className="text-xs text-muted-foreground">{field.description}</p>
                        )}
                        {validation.pattern_description && (
                          <p className="text-xs text-blue-600 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            Format: {validation.pattern_description}
                          </p>
                        )}
                        {renderFieldInput(field)}
                        {validationErrors[field.tag] && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {validationErrors[field.tag]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="p-3 bg-amber-50 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700">
            You have unsaved changes. Click "Save Changes" to update the case.
          </span>
        </div>
      )}

      {/* Calculation Audit Dialog */}
      <CalculationAuditDialog
        open={auditDialogOpen}
        onOpenChange={setAuditDialogOpen}
        caseId={caseId}
        field={auditDialogField}
        currentValue={currentCalcValue}
        sourceFields={currentSourceFields}
        token={token}
      />
    </div>
  );
}
